import type { ReactElement, ReactNode } from 'react';
import type {
  FactoryRepoDeploy,
  FactoryRepoFileGate,
  FactoryRepoPolicyItem,
  FactoryRepoRunner,
  FactoryRepoTaskCodeTitle,
} from '../../services/api/factoryRepoPolicies';

/**
 * Phase 130 (Plan 130-11, D-01, D-04, D-15) — the resolved policy of one repository, read-only.
 *
 * The merge gate and the deploy check read `.monkfactory/factory.yml` on main; this shows what they
 * resolved: where the answer came from (the file at a main sha, the safe default, or an invalid file),
 * the gate and deploy rules, the runner sidecars, whether `.monkfactory/Dockerfile` exists and the
 * status of the repository's runner rows.
 *
 * No input and no save: a rule changes by a reviewed pull request that changes the file. Used by
 * Bot Admin's policy card and the project Engineering tab. Since Plan 130-15 there is no stored policy
 * and so no `db` source. Presentation only; every rule is enforced by the server.
 */

const ALWAYS_PROTECTED = ['.github/', '.monkfactory/'];

const TASK_CODE_TITLE_LABEL: Record<FactoryRepoTaskCodeTitle, string> = {
  traceability_only: 'Traceability only',
  deploy_marks_done: 'Deploy marks the task Done',
};

const RUNNER_BADGE_CLASS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-700',
  building: 'bg-amber-100 text-amber-800',
  ready: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  stopped: 'bg-gray-100 text-gray-600',
};

const short = (sha: string | null): string => (sha ? sha.slice(0, 7) : '—');

/** The gate and deploy this view shows: the file's, when it was read and is valid. */
interface ShownRules {
  gate: FactoryRepoFileGate;
  deploy: FactoryRepoDeploy;
}

function shownRules(item: FactoryRepoPolicyItem): ShownRules | null {
  if (item.file.status === 'ok' && item.file.gate && item.file.deploy) {
    return { gate: item.file.gate, deploy: item.file.deploy };
  }
  return null;
}

function SourceLine({ item }: { item: FactoryRepoPolicyItem }): ReactElement {
  const { file } = item;
  if (file.status === 'ok') {
    return (
      <p
        className="text-sm text-gray-800"
        data-testid="policy-source"
        data-source="file"
        data-status="ok"
      >
        {`factory.yml on main at `}
        <span className="font-mono">{short(file.mainSha)}</span>
      </p>
    );
  }
  if (file.status === 'absent') {
    return (
      <p
        className="text-sm text-gray-800"
        data-testid="policy-source"
        data-source="file"
        data-status="absent"
      >
        Safe default: no .monkfactory/factory.yml
      </p>
    );
  }
  if (file.status === 'invalid') {
    return (
      <div data-testid="policy-source" data-source="file" data-status="invalid">
        <p className="text-sm font-medium text-red-700">Invalid factory.yml</p>
        {file.mainSha && (
          <p className="mt-0.5 text-xs text-gray-500">
            {`on main at `}
            <span className="font-mono">{short(file.mainSha)}</span>
          </p>
        )}
        <pre
          className="mt-1 whitespace-pre-wrap rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
          data-testid="policy-invalid-error"
        >
          {file.error ?? 'The file could not be validated.'}
        </pre>
        <p className="mt-1 text-xs text-red-700" data-testid="policy-invalid-holds">
          The gate holds every pull request for a human until a fixed file is merged.
        </p>
      </div>
    );
  }
  return (
    <p
      className="text-sm text-gray-600"
      data-testid="policy-source"
      data-source="file"
      data-status="unread"
    >
      Not read yet
    </p>
  );
}

function Row({ label, testId, children }: { label: string; testId: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1" data-testid={testId}>
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="col-span-2 text-xs text-gray-800">{children}</dd>
    </div>
  );
}

const mono = (text: string) => <span className="font-mono">{text}</span>;

function List({ values, empty }: { values: readonly string[]; empty: string }) {
  if (values.length === 0) return <span className="text-gray-500">{empty}</span>;
  return (
    <ul className="space-y-0.5">
      {values.map((v) => (
        <li key={v}>{mono(v)}</li>
      ))}
    </ul>
  );
}

function GateRules({ gate }: { gate: ShownRules['gate'] }): ReactElement {
  const prefixes = gate.protectedPrefixes;
  const withAlways = [...ALWAYS_PROTECTED, ...prefixes.filter((p) => !ALWAYS_PROTECTED.includes(p))];
  return (
    <dl className="divide-y divide-gray-100" data-testid="policy-gate">
      <Row label="Required CI contexts" testId="policy-required-contexts">
        <List
          values={gate.requiredContexts}
          empty="None, so the gate holds every pull request for a human"
        />
      </Row>
      <Row label="Protected prefixes" testId="policy-protected-prefixes">
        <ul className="space-y-0.5">
          {withAlways.map((p) => (
            <li key={p}>
              {mono(p)}
              {ALWAYS_PROTECTED.includes(p) && (
                <span className="ml-2 text-gray-500">always</span>
              )}
            </li>
          ))}
        </ul>
      </Row>
      <Row label="Protected files" testId="policy-protected-files">
        <List values={gate.protectedFiles} empty="None" />
      </Row>
      <Row label="Test change rule" testId="policy-test-rule">
        {gate.testChangeRule ? (
          <>
            {`Changes under `}
            {mono(gate.testChangeRule.sourcePrefix)}
            {` need a test matching `}
            {mono(gate.testChangeRule.testPatterns.join(', '))}
          </>
        ) : (
          <span className="text-gray-500">Off</span>
        )}
      </Row>
      <Row label="Release flag directory" testId="policy-release-flag-dir">
        {gate.releaseFlagDir ? mono(gate.releaseFlagDir) : <span className="text-gray-500">None</span>}
      </Row>
      <Row label="CLI bundle rule" testId="policy-cli-bundle">
        {gate.cliBundleRule ? 'On' : <span className="text-gray-500">Off</span>}
      </Row>
      <Row label="Task code in title" testId="policy-task-code-title">
        {TASK_CODE_TITLE_LABEL[gate.taskCodeTitle]}
      </Row>
    </dl>
  );
}

function deployText(deploy: FactoryRepoDeploy): string {
  if (deploy.kind === 'manual') {
    return deploy.creditsDoneMark ? 'Manual, credits the Done mark' : 'Manual';
  }
  const parts = [
    `CI status: production ${deploy.contexts.production}, staging ${deploy.contexts.staging}`,
  ];
  if (deploy.approvalGate) parts.push(`approval gate ${deploy.approvalGate}`);
  if (deploy.creditsDoneMark) parts.push('credits the Done mark');
  return parts.join('; ');
}

function RunnerRow({ runner }: { runner: FactoryRepoRunner }): ReactElement {
  const badge = RUNNER_BADGE_CLASS[runner.status] ?? 'bg-gray-100 text-gray-700';
  return (
    <li
      className="flex flex-wrap items-center gap-2 text-xs text-gray-700"
      data-testid={`policy-runner-${runner.paperclipCompanyId}`}
    >
      <span
        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${badge}`}
        data-testid="policy-runner-status"
      >
        {runner.status}
      </span>
      <span>
        {`built `}
        <span className="font-mono" data-testid="policy-runner-built">
          {short(runner.builtSha)}
        </span>
      </span>
      {runner.errorCode && (
        <span className="text-red-700" data-testid="policy-runner-error">
          {`error: `}
          <span className="font-mono">{runner.errorCode}</span>
        </span>
      )}
    </li>
  );
}

export interface RepoPolicyReadOnlyProps {
  item: FactoryRepoPolicyItem;
  /** Rendered under the source line (a shared-policy note, an editor of the project name). */
  note?: ReactNode;
}

/** The resolved factory.yml of one repository and its runner status. No input is rendered. */
export function RepoPolicyReadOnly({ item, note }: RepoPolicyReadOnlyProps): ReactElement {
  const rules = shownRules(item);
  const sidecars = item.file.runner?.sidecars ?? [];
  const showRunnerFacts = item.file.status === 'ok';

  return (
    <li className="py-3" data-testid={`repo-policy-readonly-${item.repository}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-sm font-medium text-gray-900">{item.repository}</span>
      </div>
      <div className="mt-1">
        <SourceLine item={item} />
      </div>
      {note}

      {rules && (
        <div className="mt-2">
          <GateRules gate={rules.gate} />
          <dl className="divide-y divide-gray-100">
            <Row label="Deploy" testId="policy-deploy">
              {deployText(rules.deploy)}
            </Row>
          </dl>
        </div>
      )}

      <dl className="mt-1 divide-y divide-gray-100">
        {showRunnerFacts && (
          <Row label="Runner sidecars" testId="policy-sidecars">
            <List values={sidecars} empty="None" />
          </Row>
        )}
        <Row label=".monkfactory/Dockerfile" testId="policy-dockerfile">
          {item.file.dockerfilePresent === null
            ? '—'
            : item.file.dockerfilePresent
              ? 'Present: yes'
              : 'Present: no'}
        </Row>
        <Row label="Runner" testId="policy-runners">
          {item.runners.length === 0 ? (
            <span className="text-gray-500">No runner yet</span>
          ) : (
            <ul className="space-y-1">
              {item.runners.map((r) => (
                <RunnerRow key={r.paperclipCompanyId} runner={r} />
              ))}
            </ul>
          )}
        </Row>
      </dl>
    </li>
  );
}

export default RepoPolicyReadOnly;
